import clsx from 'clsx'
import { useRef } from 'react'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { firstStickyColumn, formatCellDisplay, getColumnMeta, rowIsAccent } from '../reports/columnMeta'
import { reportTableWrap } from '../reports/ui'
import type { ReportTablePaginationProps, ReportTableProps } from './ReportTable.types'
import { EmptyState } from './EmptyState'

/** One chevron that rotates, rather than two icons that swap — the direction change
 *  animates instead of blinking. */
function SortIcon({ active, dir }: { active: boolean; dir?: 'asc' | 'desc' }) {
  return (
    <ChevronDown
      className={clsx(
        'h-3.5 w-3.5 transition-transform duration-150',
        active ? 'text-blue-600' : 'text-slate-300',
        active && dir === 'asc' && 'rotate-180',
      )}
    />
  )
}

const CELL_PAD = 'whitespace-nowrap px-4 py-3.5'
const HEAD_PAD = 'whitespace-nowrap px-4 py-3.5'
const STICKY_SHADOW = 'shadow-[2px_0_8px_-2px_rgba(15,23,42,0.10)]'

function stickyRowBg(selected: boolean, accent: boolean): string {
  if (selected) return 'bg-blue-50 group-hover:bg-blue-50'
  if (accent) return 'bg-[rgba(244,63,94,0.10)] group-hover:bg-[rgba(244,63,94,0.14)]'
  return 'bg-white group-hover:bg-slate-50'
}

function SkeletonRows({ cols, selectable }: { cols: number; selectable?: boolean }) {
  const n = Math.max(cols, 5)
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <tr key={i} className="bg-white" style={{ opacity: 1 - i * 0.09 }}>
          {selectable ? (
            <td className={clsx(CELL_PAD, 'w-12 sticky left-0 z-10 bg-white')}>
              <div className="skeleton h-4 w-4" />
            </td>
          ) : null}
          {Array.from({ length: n }).map((__, j) => (
            <td key={j} className={CELL_PAD}>
              <div className="skeleton h-4" style={{ width: j === 0 ? '7rem' : '5rem' }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

export function ReportTable({
  columns,
  rows,
  sort,
  onSort,
  selectable,
  selected,
  onToggleRow,
  onRowClick,
  rowOffset = 0,
  accentRow,
  columnDefs,
  loading,
  emptyTitle = 'No rows',
  emptyBody,
  compact,
  fill,
  applyLoadStateLabels = true,
  formatCell,
  className,
}: ReportTableProps) {
  const stickyCol = firstStickyColumn(columns, columnDefs)
  const pad = compact ? 'whitespace-nowrap px-4 py-2.5' : CELL_PAD
  const stickyLeft = selectable ? 'left-12' : 'left-0'
  const cellOpts = { applyLoadStateLabels }

  // Re-key the tbody whenever the row set itself changes (filter, sort, page, refetch) so
  // the entrance animation replays on the new rows. A ref comparison, not a deep one --
  // useReportTableState hands back a fresh array identity exactly when the set changed.
  const dataEpochRef = useRef(0)
  const prevRowsRef = useRef<typeof rows | null>(null)
  if (prevRowsRef.current !== rows) {
    prevRowsRef.current = rows
    dataEpochRef.current += 1
  }
  // Above ~150 rows the stagger stops reading as polish and starts costing frames.
  const animateRows = rows.length <= 150

  const wrapClass = clsx(
    reportTableWrap,
    fill && 'flex min-h-0 flex-1 flex-col',
    className,
  )
  const scrollerClass = clsx(
    'overflow-auto',
    fill ? 'min-h-0 flex-1' : 'max-h-[calc(100vh-280px)]',
  )

  if (!loading && !rows.length) {
    return (
      <div className={clsx(wrapClass, 'flex items-center justify-center py-16')}>
        <EmptyState title={emptyTitle} body={emptyBody ?? 'Nothing matches the current filters.'} />
      </div>
    )
  }

  if (loading && !columns.length) {
    return (
      <div className={clsx(wrapClass, 'px-6 py-8')} aria-busy="true" aria-live="polite">
        <p className="mb-5 font-sans text-sm text-slate-500">Loading rows…</p>
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="skeleton h-4"
              style={{ width: `${88 - (i % 4) * 10}%`, opacity: 1 - i * 0.08 }}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={wrapClass}>
      <div className={scrollerClass}>
        <table className="min-w-full divide-y divide-slate-200" aria-busy={loading || undefined}>
          <thead className="bg-slate-50">
            <tr>
              {selectable ? (
                <th
                  scope="col"
                  className={clsx(
                    HEAD_PAD, 'sticky left-0 top-0 z-30 w-12 bg-slate-50/95 backdrop-blur-sm',
                  )}
                />
              ) : null}
              {columns.map((col) => {
                const def = columnDefs?.[col]
                const meta = getColumnMeta(col)
                const sticky = col === stickyCol
                const label = def?.label ?? meta.label ?? col.replace(/_/g, ' ')
                const align = def?.align ?? meta.align ?? 'left'
                return (
                  <th
                    key={col}
                    scope="col"
                    aria-sort={
                      sort?.column === col
                        ? sort.direction === 'asc' ? 'ascending' : 'descending'
                        : 'none'
                    }
                    className={clsx(
                      HEAD_PAD,
                      'sticky top-0 bg-slate-50/95 backdrop-blur-sm text-left',
                      'font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500',
                      align === 'right' && 'text-right',
                      sticky ? clsx('z-30', stickyLeft, STICKY_SHADOW) : 'z-20',
                      onSort && 'cursor-pointer select-none transition-colors hover:text-slate-800',
                    )}
                    onClick={onSort ? () => onSort(col) : undefined}
                  >
                    <span className={clsx('inline-flex items-center gap-1.5', align === 'right' && 'w-full justify-end')}>
                      {label}
                      {onSort ? <SortIcon active={sort?.column === col} dir={sort?.direction} /> : null}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody key={dataEpochRef.current} className="divide-y divide-slate-200 bg-white">
            {loading ? (
              <SkeletonRows cols={columns.length} selectable={selectable} />
            ) : (
              rows.map((row, ri) => {
                const globalIdx = rowOffset + ri
                const isSelected = selected?.has(globalIdx) ?? false
                const accent = accentRow?.(row) ?? rowIsAccent(row)
                const pinBg = stickyRowBg(isSelected, accent)
                return (
                  <tr
                    key={globalIdx}
                    style={animateRows ? { animationDelay: `${Math.min(ri, 12) * 16}ms` } : undefined}
                    className={clsx(
                      'group transition-colors duration-100 hover:bg-slate-50',
                      animateRows && 'animate-row-in',
                      isSelected && 'bg-blue-50 hover:bg-blue-50',
                      accent && !isSelected && 'bg-[rgba(244,63,94,0.10)] hover:bg-[rgba(244,63,94,0.14)]',
                      onRowClick && 'cursor-pointer active:bg-slate-100',
                    )}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('input')) return
                      onRowClick?.(row, globalIdx)
                    }}
                  >
                    {selectable ? (
                      <td className={clsx(pad, 'sticky left-0 z-10 w-12', pinBg)}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => onToggleRow?.(globalIdx, (e.nativeEvent as MouseEvent).shiftKey)}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                        />
                      </td>
                    ) : null}
                    {columns.map((col) => {
                      const def = columnDefs?.[col]
                      const meta = getColumnMeta(col, row[col])
                      const align = def?.align ?? meta.align ?? 'left'
                      const sticky = col === stickyCol
                      const val = row[col]
                      const isId = col.includes('_id') || col === stickyCol
                      const isNumeric = align === 'right' || typeof val === 'number'
                      return (
                        <td
                          key={col}
                          className={clsx(
                            pad,
                            'text-sm',
                            sticky && clsx('sticky z-10 font-medium text-slate-900', stickyLeft, STICKY_SHADOW, pinBg),
                            !sticky && (isId || isNumeric ? 'font-mono tabular-nums text-slate-900' : 'font-sans text-slate-600'),
                            sticky && (isId || isNumeric) && 'font-mono tabular-nums',
                            align === 'right' && 'text-right',
                          )}
                        >
                          {def?.render
                            ? def.render(val, row, col)
                            : formatCell
                              ? formatCell(val, col, meta)
                              : formatCellDisplay(val, col, meta, cellOpts)}
                        </td>
                      )
                    })}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function ReportTablePagination({
  page, totalPages, pageSize, pageSizes, showingFrom, showingTo, totalCount,
  onPageChange, onPageSizeChange,
}: ReportTablePaginationProps) {
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-lg bg-white px-4 py-3 shadow-sm ring-1 ring-black/5">
      <span className="font-mono text-xs tabular-nums text-slate-500">
        {totalCount > 0 ? `Showing ${showingFrom}–${showingTo} of ${totalCount}` : '0 rows'}
      </span>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 font-sans text-xs text-slate-500">
          Page size
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-md border-0 bg-white py-1 pl-2 pr-7 font-mono text-xs tabular-nums text-slate-900 shadow-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-blue-600"
          >
            {pageSizes.map((s) => (
              <option key={s} value={s}>{s === 0 ? 'All' : s}</option>
            ))}
          </select>
        </label>
        {pageSize > 0 && totalPages > 1 ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 0}
              onClick={() => onPageChange(page - 1)}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 transition-[background-color,transform] duration-100 hover:bg-slate-50 hover:text-slate-700 active:scale-95 disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[4rem] text-center font-mono text-xs tabular-nums text-slate-500">
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => onPageChange(page + 1)}
              className="flex h-8 w-8 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm ring-1 ring-inset ring-slate-300 transition-[background-color,transform] duration-100 hover:bg-slate-50 hover:text-slate-700 active:scale-95 disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
